import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import FieldMappingInput from './targets/FieldMappingInput';

const MAX_DEDUP_FIELDS = 10;

interface DedupFieldsEditorProps {
  paths: string[];
  onChange: (paths: string[]) => void;
  sampleEvent: Record<string, unknown>;
  disabled?: boolean;
  error?: string;
}

/**
 * Picks the event fields whose values compose the dedup fingerprint. Each row
 * reuses FieldMappingInput, so the author gets the same sample-event autocomplete
 * and live "→ resolved value" preview as the channel field mappings.
 */
const DedupFieldsEditor: React.FC<DedupFieldsEditorProps> = ({
  paths,
  onChange,
  sampleEvent,
  disabled,
  error,
}) => {
  const rows = paths.length > 0 ? paths : [''];

  const setAt = (index: number, value: string) => {
    const next = [...rows];
    next[index] = value;
    onChange(next);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        One rolling action log item per distinct combination of these event fields.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rows.map((path, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <FieldMappingInput
                label={`Field ${i + 1}`}
                value={path}
                onChange={(v) => setAt(i, v)}
                sampleEvent={sampleEvent}
                disabled={disabled}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
              disabled={disabled || rows.length === 1}
              aria-label="Remove field"
              sx={{ mt: 0.5 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...rows, ''])}
        disabled={disabled || rows.length >= MAX_DEDUP_FIELDS}
        sx={{ mt: 1 }}
      >
        Add field
      </Button>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DedupFieldsEditor;
